import React, { useEffect, useState } from 'react'
import {
  MessageCircle,
  Phone,
  ArrowRight,
  CheckCircle2,
  Shield,
  BarChart3,
  Zap,
  Database,
  Workflow,
  Rocket,
  Star,
} from 'lucide-react'

function Site() {
  const [showWhatsApp, setShowWhatsApp] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      const y = window.pageYOffset
      setIsScrolled(y > 10)
      setShowWhatsApp(y > 300)
    }
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const whatsappNumber = '5517991064082'
  const whatsappMessage = 'Olá! Gostaria de saber mais sobre a plataforma de gestão.'
  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(whatsappMessage)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="app">
      {/* CSS PURO (sem Tailwind) */}
      <style>{css}</style>

      {/* NAVBAR */}
      <header className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
        <div className="container nav">
          <a href="#home" className="brand">
            <div className="brand__logo" />
            <span className="brand__name">ZipHub Gestão</span>
          </a>
          <nav className="nav__links">
            <a href="#features">Recursos</a>
            <a href="#testemunhos">Clientes</a>
            <a href="#precos">Planos</a>
            <a href="#contato">Contato</a>
          </nav>
          <div className="nav__cta">
            {/* BOTÃO LOGIN */}
            <a href="/login" className="btn btn--ghost">Login</a>

            <a href="#precos" className="btn btn--ghost">Ver planos</a>
            <button onClick={handleWhatsAppClick} className="btn btn--primary">
              Falar com time <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>


      {/* HERO */}
      <section id="home" className="hero">
        <div className="container hero__grid">
          <div className="hero__copy">
            <span className="chip chip--info">
              <Rocket size={14} /> Plataforma completa de gestão
            </span>
            <h1 className="hero__title">
              Centralize sua operação. <span className="gradient-text">Cresça com dados.</span>
            </h1>
            <p className="muted">
              ERP moderno com automação, relatórios inteligentes e integrações. Gere previsões,
              acompanhe estoque e vendas em tempo real.
            </p>
            <div className="actions">
              <a  href="/login"  className="btn btn--primary btn--lg">
                Login<ArrowRight size={18} />
              </a>
              <a href="#features" className="btn btn--ghost btn--lg">Ver recursos</a>
            </div>
            <div className="hero__badges">
              <span><Shield size={14} /> Segurança empresarial</span>
              <span><Zap size={14} /> Implementação rápida</span>
            </div>
          </div>

          <div className="hero__mock">
            <div className="mock__glow" />
            <div className="card card--elevated">
              <div className="mock__header">
                <div className="text-sm">Dashboard</div>
                <span className="chip chip--success">
                  <CheckCircle2 size={14} /> Sincronizado
                </span>
              </div>

              <div className="grid grid--kpis">
                <KpiCard title="Receita (30d)" value="R$ 248k" delta="+12.4%" />
                <KpiCard title="Pedidos" value="3.142" delta="+8.1%" />
                <KpiCard title="Clientes Ativos" value="1.284" delta="+4.7%" />
                <KpiCard title="NPS" value="72" delta="+3 pts" />
              </div>

              <div className="panel">
                <div className="text-sm fw-500 mb-8">Vendas por canal</div>
                <div className="grid grid--channels">
                  <Channel value={48} label="Loja" />
                  <Channel value={32} label="Marketplace" />
                  <Channel value={20} label="Representantes" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="section">
        <div className="container center">
          <h2 className="title">Tudo o que você precisa</h2>
          <p className="muted">Módulos essenciais para operação diária e crescimento.</p>
        </div>
        <div className="container grid grid--features mt-24">
          <Feature icon={<Database />} title="Estoque Inteligente" desc="Curva ABC, reposição e alertas de ruptura em tempo real." />
          <Feature icon={<Workflow />} title="Fluxos Automatizados" desc="Aprovações, notificações e integrações sem código." />
          <Feature icon={<BarChart3 />} title="Relatórios & BI" desc="KPIs por área, cubos e exportações fáceis." />
          <Feature icon={<Shield />} title="Segurança" desc="Permissões granulares, auditoria e LGPD." />
          <Feature icon={<Zap />} title="Performance" desc="Arquitetura rápida, filas e processamento." />
          <Feature icon={<Rocket />} title="Escalável" desc="Pronto para crescer com sua operação." />
        </div>
      </section>

      {/* TESTEMUNHOS */}
      <section id="testemunhos" className="section section--soft">
        <div className="container center">
          <h2 className="title">Clientes que recomendam</h2>
          <p className="muted">Resultados reais com simplicidade de uso.</p>
        </div>
        <div className="container grid grid--cards mt-24">
          <Testimonial
            name="Marcos Andrade"
            role="COO, VarejoMix"
            text="Saímos de planilhas para um fluxo 100% integrado. Reduzimos rupturas em 31%."
          />
          <Testimonial
            name="Talita Sousa"
            role="CFO, Grupo Norte"
            text="Fechamento 3x mais rápido e governança dos centros de custo."
          />
          <Testimonial
            name="Henrique Lima"
            role="Head Comercial, OnStore"
            text="Relatórios do jeito que o time precisava. Adoção foi imediata."
          />
        </div>
      </section>

      {/* PREÇOS */}
      <section id="precos" className="section">
        <div className="container center">
          <h2 className="title">Planos flexíveis</h2>
          <p className="muted">Escolha o melhor ponto de partida. Sem letras miúdas.</p>
        </div>
        <div className="container grid grid--pricing mt-24">
          <PriceCard
            badge="Essencial"
            price="R$ 249"
            period="/mês"
            features={['Até 5 usuários', 'Estoque & Vendas', 'Relatórios básicos', 'Suporte por e-mail']}
          />
          <PriceCard
            highlight
            badge="Mais popular"
            price="R$ 599"
            period="/mês"
            features={['Até 20 usuários', 'Automação de fluxos', 'Dashboards & BI', 'Integrações marketplace', 'Suporte prioritário']}
          />
          <PriceCard
            badge="Enterprise"
            price="Sob consulta"
            features={['Usuários ilimitados', 'SLA dedicado', 'Onboarding assistido', 'Recursos avançados']}
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contato" className="footer">
        <div className="container footer__grid">
          <div className="brand">
            <div className="brand__logo" />
            <div>
              <p className="fw-600">ZipHub Gestão</p>
              <p className="muted">Plataforma completa de gestão empresarial.</p>
            </div>
          </div>
          <div className="muted">© {new Date().getFullYear()} ZipHub. Todos os direitos reservados.</div>
        </div>
      </footer>

      {/* WHATSAPP FLOAT */}
      <div className={`wa ${showWhatsApp ? 'wa--show' : ''}`}>
        <div className="wa__tooltip">Fale conosco no WhatsApp</div>
        <button onClick={handleWhatsAppClick} aria-label="Contato via WhatsApp" className="wa__btn">
          <div className="wa__ping" />
          <MessageCircle size={24} className="wa__icon" />
          <div className="wa__ring" />
        </button>
        <div className="wa__pulse" />
      </div>

      {/* Quick Contact (Ligação) */}
      <div className={`call ${showWhatsApp ? 'call--show' : ''}`}>
        <button onClick={() => window.open(`tel:+${whatsappNumber}`, '_self')} aria-label="Fazer ligação" className="call__btn">
          <Phone size={20} />
        </button>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`totop ${isScrolled ? 'totop--show' : ''}`}
        aria-label="Voltar ao topo"
      >
        <svg className="totop__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  )
}

/* ---------- Subcomponentes ---------- */

function KpiCard({ title, value, delta }) {
  return (
    <div className="card">
      <div className="text-xs muted">{title}</div>
      <div className="kpi">
        <div className="kpi__value">{value}</div>
        <span className="kpi__delta">{delta}</span>
      </div>
      <div className="bar">
        <div className="bar__fill" style={{ width: '75%' }} />
      </div>
    </div>
  )
}

function Channel({ value, label }) {
  return (
    <div className="channel">
      <div className="channel__row">
        <span className="muted">{label}</span>
        <span className="fw-500">{value}%</span>
      </div>
      <div className="bar bar--thin">
        <div className="bar__fill" style={{ width: `${value}%` }} />
      </div>
    </div>
  )
}

function Feature({ icon, title, desc }) {
  return (
    <div className="feature">
      <div className="feature__icon">{React.cloneElement(icon, { size: 20 })}</div>
      <h3 className="feature__title">{title}</h3>
      <p className="muted">{desc}</p>
      <div className="feature__more">Saiba mais <ArrowRight size={16} /></div>
    </div>
  )
}

function Testimonial({ name, role, text }) {
  return (
    <div className="tcard">
      <div className="tcard__star"><Star className="tcard__starIcon" /></div>
      <p>“{text}”</p>
      <div className="tcard__person">
        <div className="avatar" />
        <div>
          <div className="fw-600">{name}</div>
          <div className="text-xs muted">{role}</div>
        </div>
      </div>
    </div>
  )
}

function PriceCard({ highlight, badge, price, period, features = [] }) {
  return (
    <div className={`pcard ${highlight ? 'pcard--hl' : ''}`}>
      <div className={`badge ${highlight ? 'badge--primary' : 'badge--ghost'}`}>{badge}</div>
      <div className="pprice">
        <div className="pprice__value">{price}</div>
        {period && <div className="pprice__period">{period}</div>}
      </div>
      <ul className="plist">
        {features.map((f, i) => (
          <li key={i} className="plist__item">
            <CheckCircle2 size={18} className="plist__icon" /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => (window.location.hash = '#contato')}
        className={`btn ${highlight ? 'btn--primary' : 'btn--ghost'} btn--full mt-16`}
      >
        {highlight ? 'Assinar agora' : 'Fale com vendas'}
      </button>
    </div>
  )
}

/* ---------- CSS PURO ---------- */

const css = `
:root{
  --bg:#f6f7fb; --card:#fff; --text:#111827; --muted:#6b7280;
  --blue:#2563eb; --blue-600:#2563eb; --blue-700:#1d4ed8; --indigo:#4f46e5;
  --green:#16a34a; --green-300:#86efac; --green-50:#ecfdf5;
  --border:#e5e7eb; --shadow:0 10px 30px rgba(37,99,235,.12);
}

*{box-sizing:border-box}
html,body,#root,.app{height:100%}
body{margin:0; font-family:ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Inter, Arial; color:var(--text); background:var(--bg)}
a{color:inherit; text-decoration:none}

.container{max-width:1200px; margin:0 auto; padding:0 16px}
.center{text-align:center}
.mt-24{margin-top:24px}
.mb-8{margin-bottom:8px}
.text-sm{font-size:14px}
.text-xs{font-size:12px}
.fw-500{font-weight:500}
.fw-600{font-weight:600}
.muted{color:var(--muted)}

.btn{display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:12px; border:1px solid transparent; background:transparent; cursor:pointer; transition:.2s; font-weight:500}
.btn--lg{padding:12px 18px; font-size:15px}
.btn--primary{background:var(--blue); color:#fff; box-shadow:var(--shadow)}
.btn--primary:hover{background:var(--blue-700)}
.btn--ghost{border-color:var(--border); background:#fff}
.btn--ghost:hover{border-color:var(--blue); color:var(--blue)}
.btn--full{width:100%}

.badge{display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-size:12px; border:1px solid var(--border); background:#fff; color:var(--muted)}
.badge--primary{background:var(--blue); color:#fff; border-color:transparent}
.badge--ghost{background:#fff}

.chip{display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-size:12px; border:1px solid #c7d2fe; background:#eef2ff; color:#3730a3}
.chip--success{border-color:#bbf7d0; background:var(--green-50); color:#065f46}
.chip--info{border-color:#bfdbfe; background:#eff6ff; color:#1e40af}

.card{background:var(--card); border:1px solid var(--border); border-radius:20px; padding:16px}
.card--elevated{box-shadow:var(--shadow)}

.panel{border:1px solid var(--border); border-radius:16px; padding:16px; margin-top:16px}

.grid{display:grid; gap:16px}
.grid--kpis{grid-template-columns:repeat(2,minmax(0,1fr))}
.grid--channels{grid-template-columns:repeat(3,minmax(0,1fr))}
.grid--features{grid-template-columns:repeat(3,minmax(0,1fr))}
.grid--cards{grid-template-columns:repeat(3,minmax(0,1fr))}
.grid--pricing{grid-template-columns:repeat(3,minmax(0,1fr))}
@media (max-width:1024px){
  .grid--features,.grid--cards,.grid--pricing{grid-template-columns:repeat(2,minmax(0,1fr))}
  .grid--channels{grid-template-columns:repeat(3,minmax(0,1fr))}
}
@media (max-width:640px){
  .grid--kpis{grid-template-columns:1fr}
  .grid--channels{grid-template-columns:1fr}
  .grid--features,.grid--cards,.grid--pricing{grid-template-columns:1fr}
}

.title{font-size:32px; font-weight:700}
.gradient-text{background:linear-gradient(90deg,var(--blue),var(--indigo)); -webkit-background-clip:text; background-clip:text; color:transparent}

.navbar{position:fixed; top:0; left:0; right:0; z-index:40; transition:.25s; background:transparent}
.navbar--scrolled{backdrop-filter:blur(10px); background:rgba(255,255,255,.7); border-bottom:1px solid var(--border)}
.nav{height:64px; display:flex; align-items:center; justify-content:space-between}
.nav__links{display:none; gap:24px}
.nav__cta{display:none; gap:12px}
@media (min-width:768px){ .nav__links{display:flex} .nav__cta{display:flex} }
.nav a:hover{color:var(--blue)}

.brand{display:flex; align-items:center; gap:10px}
.brand__logo{width:32px; height:32px; border-radius:12px; background:linear-gradient(135deg,var(--blue),var(--indigo))}
.brand__name{font-weight:600; letter-spacing:.2px}

.hero{padding-top:112px; padding-bottom:48px; position:relative; overflow:hidden; background:radial-gradient(ellipse at top, #eef2ff 0, #fff 60%)}
.hero__grid{display:grid; gap:32px; align-items:center; grid-template-columns:1fr}
@media (min-width:1024px){ .hero__grid{grid-template-columns:1fr 1fr} }
.hero__title{font-size:38px; font-weight:800; line-height:1.1; margin:16px 0}
.actions{display:flex; gap:12px; flex-wrap:wrap; margin-top:16px}
.hero__badges{display:flex; gap:16px; align-items:center; margin-top:16px; color:var(--muted); font-size:12px}

.hero__mock{position:relative}
.mock__glow{position:absolute; inset:-16px; border-radius:24px; background:linear-gradient(135deg,#dbeafe,#e0e7ff); filter:blur(20px); z-index:-1}

.kpi{display:flex; align-items:baseline; gap:10px; margin-top:4px}
.kpi__value{font-size:24px; font-weight:600}
.kpi__delta{font-size:12px; color:#047857}

.bar{height:8px; border-radius:999px; background:#f2f4f7; overflow:hidden; margin-top:12px}
.bar--thin{height:6px}
.bar__fill{height:100%; background:linear-gradient(90deg,var(--blue),var(--indigo))}

.channel{border:1px solid var(--border); border-radius:12px; padding:12px}
.channel__row{display:flex; align-items:center; justify-content:space-between}

.feature{border:1px solid var(--border); background:#fff; border-radius:20px; padding:20px; transition:.2s}
.feature:hover{transform:translateY(-2px); box-shadow:0 10px 24px rgba(0,0,0,.04), var(--shadow)}
.feature__icon{display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:12px; background:#eff6ff; color:#1e40af; border:1px solid #dbeafe; margin-bottom:12px}
.feature__title{font-size:18px; font-weight:600; margin:2px 0 6px}
.feature__more{margin-top:10px; color:#1e40af; opacity:0; transition:.2s; display:flex; align-items:center; gap:6px}
.feature:hover .feature__more{opacity:1}

.section{padding:72px 0}
.section--soft{padding:72px 0; background:radial-gradient(ellipse at bottom, #fff 0, #eff6ff 60%, #fff 100%)}

.tcard{position:relative; overflow:hidden; border:1px solid var(--border); background:rgba(255,255,255,.85); backdrop-filter:blur(6px); border-radius:20px; padding:20px; transition:.2s}
.tcard:hover{box-shadow:0 10px 24px rgba(0,0,0,.05)}
.tcard__star{position:absolute; top:-24px; right:-24px; transform:rotate(12deg); opacity:.12}
.tcard__starIcon{width:80px; height:80px; color:#f59e0b}
.tcard__person{display:flex; align-items:center; gap:12px; margin-top:14px}
.avatar{width:40px; height:40px; border-radius:999px; background:linear-gradient(135deg,var(--blue),var(--indigo))}

.pcard{position:relative; border:1px solid var(--border); background:#fff; border-radius:20px; padding:20px}
.pcard--hl{border-color:#c7d2fe; box-shadow:0 20px 40px rgba(79,70,229,.15)}
.pprice{display:flex; align-items:flex-end; gap:6px; margin-top:8px}
.pprice__value{font-size:28px; font-weight:700}
.pprice__period{font-size:12px; color:var(--muted); padding-bottom:4px}
.plist{list-style:none; padding:0; margin:16px 0 0}
.plist__item{display:flex; align-items:flex-start; gap:8px; font-size:14px; margin-bottom:10px}
.plist__icon{color:#059669}

.footer{border-top:1px solid var(--border); background:#fff; padding:24px 0}
.footer__grid{display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap}

.wa{position:fixed; right:24px; bottom:24px; z-index:50; transform:translateY(64px); opacity:0; transition:.4s}
.wa--show{transform:translateY(0); opacity:1}
.wa__btn{position:relative; width:56px; height:56px; border:none; border-radius:999px; background:#22c55e; color:#fff; cursor:pointer; box-shadow:0 10px 24px rgba(34,197,94,.35); transition:.2s}
.wa__btn:hover{transform:scale(1.08); background:#16a34a}
.wa__icon{position:relative; z-index:2}
.wa__ring{position:absolute; inset:0; border:2px solid #86efac; border-radius:999px; opacity:0; transform:scale(1.1); transition:.2s}
.wa__btn:hover .wa__ring{opacity:1}
.wa__ping{position:absolute; inset:0; border-radius:999px; background:#4ade80; opacity:.2; animation:ping 1.5s infinite}
.wa__pulse{position:absolute; inset:0; border:4px solid #86efac; border-radius:999px; opacity:.3; animation:pulse 2s infinite}
.wa__tooltip{position:absolute; right:0; bottom:64px; background:#111827; color:#fff; padding:6px 10px; border-radius:8px; white-space:nowrap; font-size:12px; opacity:0; transform:translateY(6px); pointer-events:none; transition:.2s}
.wa:hover .wa__tooltip{opacity:1; transform:translateY(0)}

.call{position:fixed; right:24px; bottom:112px; z-index:40; transform:translateY(12px); opacity:0; transition:.3s; pointer-events:none}
.call--show{transform:translateY(0); opacity:1; pointer-events:auto}
.call__btn{width:48px; height:48px; border:none; border-radius:999px; background:#2563eb; color:#fff; box-shadow:0 8px 20px rgba(37,99,235,.35); cursor:pointer; transition:.2s}
.call__btn:hover{transform:scale(1.06); background:#1d4ed8}

.totop{position:fixed; left:24px; bottom:24px; width:48px; height:48px; border:none; border-radius:999px; background:#111827; color:#fff; box-shadow:0 8px 20px rgba(0,0,0,.25); cursor:pointer; transform:translateY(64px); opacity:0; transition:.4s}
.totop--show{transform:translateY(0); opacity:1}
.totop__icon{width:20px; height:20px}

@keyframes ping{75%{transform:scale(1.4); opacity:0} 100%{opacity:0}}
@keyframes pulse{0%{transform:scale(1); opacity:.35} 100%{transform:scale(1.15); opacity:0}}
`

export default Site
